/**
 * Laget: who the person works with (owner's employee UI brief, 2026-09-22, screens 5 and 6).
 * People first: today grouped into on shift now, later today and done earlier, then everybody
 * else in the company, each with the work phone and e-mail every colleague may see
 * (User.colleagueView, owner decision 2026-09-20). Uke is the denser secondary mode. A person
 * opens Kollegaprofil in a drawer: contact, today's and the coming shifts, the next shift you
 * share. No chat: the web app has none (parked in docs/handbook/_parked/employee-ui.md).
 *
 * Shifts come from the schedule route, which answers an employee with published shifts only,
 * planned fields only, and nothing when the company has turned colleagues' shifts off. "På vakt"
 * is therefore the plan, not a punch: the route does not tell an employee who has clocked in.
 */
import { type ApiShift, type ApiUser, useMyShifts, useShifts, useUsers } from '@/api/data';
import { useAuth } from '@/auth/store';
import { T } from '@/i18n';
import { today } from '@/rules';
import { useTenant } from '@/shell/tenant';
import { Chip, Drawer, Empty, Skeleton } from '@/ui';
import { DateJump } from '@/ui/DateJump';
import { DN, WeekNav, fmtD, fmtPhone, fullName } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { addDays, dow, mondayOf } from '@timefly/core';
import { type ReactNode, useState } from 'react';
import { Avatar, Card, Contact, DateBox, dayOf, longDay, shiftName, span, useNow } from './parts';

type Person = Pick<ApiUser, 'id' | 'name' | 'lastname'> & Partial<ApiUser>;

const roleOf = (u: Person | undefined) => u?.title || (u?.teams ?? []).map((t) => t.name).join(', ');

function PersonRow({
  u,
  shift,
  onOpen,
}: {
  u: Person;
  shift?: ApiShift | undefined;
  onOpen: () => void;
}) {
  return (
    <div className="eperson row" data-testid="person">
      <button type="button" className="lnkrow" onClick={onOpen}>
        <Avatar u={u} lg />
        <span className="ebody">
          <b>{fullName(u)}</b>
          <span>{roleOf(u) || ' '}</span>
        </span>
      </button>
      {shift && (
        <span className="etime">
          <span>
            <Icon name="clock" /> <span className="mono">{span(shift)}</span>
          </span>
          <small>{shiftName(shift)}</small>
        </span>
      )}
      <Contact phone={u.phone} email={u.email} />
    </div>
  );
}

function Section({
  title,
  tone,
  count,
  children,
}: {
  title: string;
  tone: 'ok' | 'warn' | 'neu';
  count: number;
  children: ReactNode;
}) {
  return (
    <Card
      title={
        <>
          {title}{' '}
          <Chip tone={tone} small>
            {T('{n} stk', { n: count })}
          </Chip>
        </>
      }
      testid={`sec-${title}`}
    >
      {children}
    </Card>
  );
}

export default function Laget() {
  const user = useAuth((s) => s.user);
  const { tenant, workspace } = useTenant();
  const day0 = today();
  const now = useNow().toISOString();
  const [mode, setMode] = useState<'dag' | 'uke'>('dag');
  const [day, setDay] = useState(day0);
  const [monday, setMonday] = useState(() => mondayOf(day0));
  const [who, setWho] = useState<Person | null>(null);
  const see = !!tenant && workspace.enabled.scheduling && workspace.visibility.coworker_shifts;
  const usersQ = useUsers();
  const from = mode === 'dag' ? day : monday;
  const to = mode === 'dag' ? day : addDays(monday, 6);
  const viewQ = useShifts(from, to, see);
  const aheadQ = useShifts(day0, addDays(day0, 13), see);
  const mineQ = useMyShifts(day0, addDays(day0, 13));

  const people = (usersQ.data ?? []).filter((u) => u.id !== user?.id && u.active_status !== false);
  const byId = new Map<number, Person>(people.map((u) => [u.id, u]));
  const theirs = (q: typeof viewQ) =>
    see
      ? (q.data?.shifts ?? []).filter((s) => s.published && s.user_id != null && s.user_id !== user?.id)
      : [];
  const view = theirs(viewQ).sort((a, b) => a.start_time.localeCompare(b.start_time));
  const personOf = (s: ApiShift): Person => byId.get(s.user_id as number) ?? (s.user as Person);
  const onDay = view.filter((s) => dayOf(s) === day);
  const isToday = day === day0;
  const nowOn = isToday ? onDay.filter((s) => s.start_time <= now && s.end_time > now) : [];
  const laterOn = isToday ? onDay.filter((s) => s.start_time > now) : onDay;
  const doneOn = isToday ? onDay.filter((s) => s.end_time <= now) : [];
  const working = new Set(onDay.map((s) => s.user_id));
  const off = people.filter((u) => !working.has(u.id));
  const rows = (list: ApiShift[]) =>
    list.map((s) => <PersonRow key={s.id} u={personOf(s)} shift={s} onOpen={() => setWho(personOf(s))} />);

  // Kollegaprofil
  const ahead = theirs(aheadQ).filter((s) => s.user_id === who?.id);
  const todayShift = ahead.find((s) => dayOf(s) === day0);
  const coming = ahead.filter((s) => dayOf(s) > day0).slice(0, 5);
  const mine = mineQ.data?.shifts ?? [];
  const together = ahead.find((s) =>
    mine.some((m) => m.start_time < s.end_time && m.end_time > s.start_time),
  );
  const status =
    !who || !see ? null : todayShift && todayShift.start_time <= now && todayShift.end_time > now ? (
      <Chip tone="ok" small>
        {T('På vakt nå')}
      </Chip>
    ) : todayShift && todayShift.start_time > now ? (
      <Chip tone="warn" small>
        {T('Senere i dag')}
      </Chip>
    ) : (
      <Chip tone="neu" small>
        {T('Ikke på vakt nå')}
      </Chip>
    );

  return (
    <>
      <div className="ph">
        <div>
          <h1>{T('Laget')}</h1>
          <div className="sub">{tenant?.name ?? ''}</div>
        </div>
        <span className="sp" />
        {see && (
          <>
            <fieldset className="seg" aria-label={T('Visning')}>
              <button type="button" aria-pressed={mode === 'dag'} onClick={() => setMode('dag')}>
                {T('Dag')}
              </button>
              <button type="button" aria-pressed={mode === 'uke'} onClick={() => setMode('uke')}>
                {T('Uke')}
              </button>
            </fieldset>
            {mode === 'dag' ? (
              <div className="wk">
                <button type="button" onClick={() => setDay(addDays(day, -1))} aria-label={T('Forrige dag')}>
                  <Icon name="chevronLeft" />
                </button>
                <DateJump value={day} onChange={setDay}>
                  <span className="lbl">{`${DN[dow(day)]} ${fmtD(day)}`}</span>
                </DateJump>
                <button type="button" onClick={() => setDay(addDays(day, 1))} aria-label={T('Neste dag')}>
                  <Icon name="chevronRight" />
                </button>
                {!isToday && (
                  <button type="button" style={{ fontSize: 12 }} onClick={() => setDay(day0)}>
                    {T('I dag')}
                  </button>
                )}
              </div>
            ) : (
              <WeekNav monday={monday} onChange={setMonday} />
            )}
          </>
        )}
      </div>

      {!see && (
        <div className="al info" data-testid="no-coworker-shifts">
          <span>
            <Icon name="info" />
          </span>
          <span>
            {workspace.enabled.scheduling
              ? T(
                  'Bedriften har slått av at ansatte ser hverandres vakter. Du ser navn og kontaktinformasjon.',
                )
              : T('Bedriften bruker ikke vaktplan. Du ser navn og kontaktinformasjon.')}
          </span>
        </div>
      )}

      {usersQ.isPending || (see && viewQ.isPending) ? (
        <Skeleton />
      ) : see && mode === 'uke' ? (
        Array.from({ length: 7 }, (_, i) => addDays(monday, i)).map((d) => {
          const list = view.filter((s) => dayOf(s) === d);
          return (
            <Card key={d} title={longDay(d)} testid={`lag-${d}`}>
              {list.length ? (
                rows(list)
              ) : (
                <p className="hint" style={{ margin: 0 }}>
                  {T('Ingen publiserte vakter denne dagen.')}
                </p>
              )}
            </Card>
          );
        })
      ) : (
        <div className="egrid">
          <div>
            {see && isToday && (
              <Section title={T('På vakt nå')} tone="ok" count={nowOn.length}>
                {nowOn.length ? rows(nowOn) : <p className="hint">{T('Ingen er på vakt akkurat nå.')}</p>}
              </Section>
            )}
            {see && laterOn.length > 0 && (
              <Section title={isToday ? T('Senere i dag') : T('På vakt')} tone="warn" count={laterOn.length}>
                {rows(laterOn)}
              </Section>
            )}
            {see && !isToday && laterOn.length === 0 && (
              <Card title={T('På vakt')}>
                <p className="hint" style={{ margin: 0 }}>
                  {T('Ingen publiserte vakter denne dagen.')}
                </p>
              </Card>
            )}
            {see && doneOn.length > 0 && (
              <Section title={T('Ferdig tidligere i dag')} tone="neu" count={doneOn.length}>
                {rows(doneOn)}
              </Section>
            )}
            <Section title={see ? T('Ikke på vakt') : T('Kolleger')} tone="neu" count={off.length}>
              {off.length ? (
                off.map((u) => <PersonRow key={u.id} u={u} onOpen={() => setWho(u)} />)
              ) : (
                <Empty>{T('Ingen andre i bedriften.')}</Empty>
              )}
            </Section>
          </div>
          {see && isToday && (
            <Card title={T('Ditt team i dag')} sub={fmtD(day0)} testid="team-today">
              <div className="ekv">
                <Icon name="users" />
                <span>{T('På vakt nå')}</span>
                <b>{nowOn.length}</b>
              </div>
              <div className="ekv">
                <Icon name="clock" />
                <span>{T('Starter senere')}</span>
                <b>{laterOn.length}</b>
              </div>
              <div className="ekv">
                <Icon name="check" />
                <span>{T('Ferdig for i dag')}</span>
                <b>{doneOn.length}</b>
              </div>
              <p className="hint">{T('Trykk på en person for kontaktinfo og kommende vakter.')}</p>
            </Card>
          )}
        </div>
      )}

      <Drawer
        open={!!who}
        onClose={() => setWho(null)}
        title={T('Kollegaprofil')}
        sub={who ? fullName(who) : ''}
      >
        {who && (
          <div data-testid="colleague">
            <div className="eprof">
              <Avatar u={who} lg />
              <span className="ebody">
                <b>{fullName(who)}</b>
                <span>{roleOf(who)}</span>
                {status}
              </span>
            </div>
            {who.phone || who.email ? (
              <>
                {who.phone && (
                  <div className="prow">
                    <b>{T('Telefon')}</b>
                    <span className="pv">{fmtPhone(who.phone)}</span>
                  </div>
                )}
                {who.email && (
                  <div className="prow">
                    <b>{T('E-post')}</b>
                    <span className="pv">{who.email}</span>
                  </div>
                )}
                <div className="eact">
                  <Contact phone={who.phone} email={who.email} big />
                </div>
              </>
            ) : (
              <p className="hint">{T('Ingen kontaktinformasjon registrert.')}</p>
            )}
            {see && (
              <>
                <section className="edsec">
                  <h3>{T('Dagens vakt')}</h3>
                  {todayShift ? (
                    <p style={{ margin: 0 }}>
                      <span className="mono">{span(todayShift)}</span> · {shiftName(todayShift)}
                    </p>
                  ) : (
                    <p className="hint">{T('Ingen vakt i dag.')}</p>
                  )}
                </section>
                {together && (
                  <div className="al ok" data-testid="together">
                    <span>
                      <Icon name="check" />
                    </span>
                    <span>
                      {T('Dere jobber samtidig')} {longDay(dayOf(together))}, {span(together)}.
                    </span>
                  </div>
                )}
                <section className="edsec">
                  <h3>{T('Kommende vakter')}</h3>
                  {coming.length ? (
                    coming.map((s) => (
                      <div className="erow static" key={s.id}>
                        <DateBox d={dayOf(s)} />
                        <span className="ebody">
                          <b className="mono">{span(s)}</b>
                          <span>{shiftName(s)}</span>
                        </span>
                      </div>
                    ))
                  ) : (
                    <p className="hint">{T('Ingen publiserte vakter de neste to ukene.')}</p>
                  )}
                </section>
              </>
            )}
          </div>
        )}
      </Drawer>
    </>
  );
}
